import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { rupiah, monthLabel, currentMonth, PAYMENT_STATUS, waLink, todayStr, dateLabel } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Wallet, MessageCircle, AlertTriangle, Zap, Wrench } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { amount: "", paid_date: todayStr(), method: "Transfer", note: "" };

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [statusF, setStatusF] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [fixing, setFixing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [fixAmount, setFixAmount] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { month };
    if (statusF !== "all") params.status = statusF;
    Promise.all([api.get("/payments", { params }), api.get("/students", { params: { status: "aktif" } })])
      .then(([p, s]) => { setPayments(p.data); setStudents(s.data); }).finally(() => setLoading(false));
  };
  useEffect(load, [month, statusF]);

  const openPay = (p) => { setEditing(p); setForm({ ...emptyForm, amount: p.remaining }); };
  const amountNum = Number(form.amount) || 0;
  const exceeds = editing && amountNum > editing.remaining;

  const savePay = async () => {
    if (amountNum <= 0) return toast.error("Nominal harus lebih dari 0");
    if (exceeds) return toast.error("Nominal melebihi sisa tagihan.");
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/${editing.id}/pay`, { ...form, amount: amountNum });
      if (data.wa_confirmation?.sent) toast.success("Pembayaran LUNAS · konfirmasi WA terkirim ke wali ✅");
      else if (data.status === "lunas" && data.wa_confirmation && data.wa_confirmation.reason !== "nonaktif") toast.warning(`Lunas tersimpan, WA gagal: ${data.wa_confirmation.reason}`);
      else toast.success(data.status === "lunas" ? "Pembayaran LUNAS ✅" : `Pembayaran ${rupiah(amountNum)} tercatat · sisa ${rupiah(data.remaining)}`);
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveFix = async () => {
    setBusy(true);
    try {
      await api.put(`/payments/${fixing.id}`, { amount_paid: Number(fixAmount) });
      toast.success("Data pembayaran dikoreksi"); setFixing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const waReminder = (p) => {
    const st = students.find((s) => s.id === p.student_id);
    const text = `Assalamu'alaikum Bapak/Ibu wali dari ${p.student_name} 🙏\n\nKami informasikan iuran les *Bimbel Gemma* bulan ${monthLabel(p.month)} sebesar Rp ${Number(p.amount_due).toLocaleString("id-ID")} ${p.status === "lunas" ? "telah *LUNAS*, terima kasih 💛" : `akan jatuh tempo tanggal 15. Sisa tagihan Rp ${Number(p.remaining).toLocaleString("id-ID")}. Terima kasih 💛`}`;
    window.open(waLink(st?.wa, text), "_blank");
  };

  const totalCollected = payments.reduce((a, p) => a + (p.amount_paid || 0), 0);
  const totalTarget = payments.reduce((a, p) => a + (p.amount_due || 0), 0);
  const flagged = payments.filter((p) => p.over_paid).length;

  return (
    <div className="space-y-6 animate-float-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Iuran & Pembayaran</h1>
          <p className="text-slate-500 mt-1">Rp 150.000/bulan · jatuh tempo tanggal 15</p>
        </div>
        <div data-testid="auto-invoice-info" className="flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
          <Zap className="w-4 h-4" /> Tagihan dibuat otomatis setiap tanggal 5 untuk semua siswa aktif
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card className="p-4 rounded-2xl border-amber-100"><div className="text-xs text-slate-400">Target</div><div className="font-display text-2xl font-bold text-slate-900">{rupiah(totalTarget)}</div></Card>
        <Card className="p-4 rounded-2xl border-emerald-100 bg-emerald-50/40"><div className="text-xs text-slate-400">Terkumpul</div><div className="font-display text-2xl font-bold text-emerald-600">{rupiah(totalCollected)}</div></Card>
        <Card className="p-4 rounded-2xl border-rose-100 bg-rose-50/40"><div className="text-xs text-slate-400">Kurang</div><div className="font-display text-2xl font-bold text-rose-600">{rupiah(Math.max(totalTarget - totalCollected, 0))}</div></Card>
      </div>

      {flagged > 0 && (
        <div data-testid="overpaid-alert" className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
          <AlertTriangle className="w-4 h-4" /> {flagged} data pembayaran melebihi nominal tagihan. Mohon review dan koreksi manual.
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <div><Label className="text-xs text-slate-400">Bulan</Label><Input type="month" data-testid="payment-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-xl border-amber-200 w-44 mt-1" /></div>
        <div>
          <Label className="text-xs text-slate-400">Status</Label>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger data-testid="payment-status-filter" className="w-44 rounded-xl border-amber-200 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {Object.entries(PAYMENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div> :
        payments.length === 0 ? <Card className="p-12 text-center rounded-2xl border-amber-100 text-slate-400">Belum ada tagihan untuk {monthLabel(month)}. Tagihan bulan berjalan dibuat otomatis pada tanggal 5.</Card> :
        <div className="space-y-2">
          {payments.map((p) => (
            <Card key={p.id} data-testid={`payment-row-${p.id}`} className={`p-4 rounded-2xl ${p.over_paid ? "border-rose-300 bg-rose-50/40" : "border-amber-100"}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0"><Wallet className="w-5 h-5 text-orange-600" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate flex items-center gap-2">{p.student_name}
                    {p.over_paid && <Badge data-testid={`overpaid-flag-${p.id}`} className="rounded-full border-0 bg-rose-500 text-white text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" /> Melebihi tagihan</Badge>}
                  </div>
                  <div className="text-xs text-slate-400">{monthLabel(p.month)} · {rupiah(p.amount_paid)} / {rupiah(p.amount_due)}{p.remaining > 0 && <span className="text-rose-500"> · sisa {rupiah(p.remaining)}</span>}</div>
                  {p.history?.length > 0 && (
                    <div data-testid={`payment-history-${p.id}`} className="mt-1.5 flex flex-wrap gap-1">
                      {p.history.map((h) => (
                        <span key={h.id} title={h.note} className={`text-[10px] px-2 py-0.5 rounded-full border ${h.correction ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {dateLabel(h.paid_date)} · {rupiah(h.amount)}{h.method ? ` · ${h.method}` : ""}{h.note ? ` · ${h.note}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <Badge data-testid={`payment-status-${p.id}`} variant="outline" className={`rounded-full ${PAYMENT_STATUS[p.status]?.cls}`}>{PAYMENT_STATUS[p.status]?.label}</Badge>
                <div className="flex gap-1.5">
                  {p.over_paid ? (
                    <Button size="sm" data-testid={`fix-btn-${p.id}`} onClick={() => { setFixing(p); setFixAmount(p.amount_due); }} className="rounded-lg bg-rose-500 hover:bg-rose-600 h-8"><Wrench className="w-3.5 h-3.5 mr-1" /> Koreksi</Button>
                  ) : (
                    <Button size="sm" data-testid={`pay-btn-${p.id}`} disabled={p.status === "lunas"} onClick={() => openPay(p)}
                      className={`rounded-lg h-8 ${p.status === "lunas" ? "bg-slate-200 text-slate-400 cursor-not-allowed hover:bg-slate-200" : "bg-emerald-500 hover:bg-emerald-600"}`}>Bayar</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => waReminder(p)} className="rounded-lg text-emerald-600 h-8 px-2"><MessageCircle className="w-4 h-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      }

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-2xl" data-testid="pay-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Catat Pembayaran · {editing?.student_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="rounded-xl bg-slate-50 p-2"><div className="text-[10px] text-slate-400">Tagihan</div><div className="font-bold">{rupiah(editing?.amount_due)}</div></div>
              <div className="rounded-xl bg-emerald-50 p-2"><div className="text-[10px] text-slate-400">Sudah Dibayar</div><div className="font-bold text-emerald-600">{rupiah(editing?.amount_paid)}</div></div>
              <div className="rounded-xl bg-rose-50 p-2"><div className="text-[10px] text-slate-400">Sisa</div><div className="font-bold text-rose-600" data-testid="pay-remaining">{rupiah(editing?.remaining)}</div></div>
            </div>
            <div>
              <Label>Nominal Dibayar</Label>
              <Input type="number" data-testid="pay-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`mt-1 rounded-xl ${exceeds ? "border-rose-400 focus-visible:ring-rose-400" : ""}`} />
              {exceeds && <p data-testid="pay-amount-error" className="text-xs text-rose-600 mt-1">Nominal melebihi sisa tagihan.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal Bayar</Label><Input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} className="mt-1 rounded-xl" /></div>
              <div>
                <Label>Metode</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Transfer">Transfer</SelectItem><SelectItem value="Tunai">Tunai</SelectItem><SelectItem value="QRIS">QRIS</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Catatan / Keterangan</Label><Input data-testid="pay-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="cth: cicilan 1, transfer BCA" className="mt-1 rounded-xl" /></div>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="pay-full-btn" onClick={() => setForm({ ...form, amount: editing?.remaining })} className="rounded-xl flex-1">Bayar Sisa</Button>
              <Button variant="outline" onClick={() => setForm({ ...form, amount: Math.round((editing?.remaining || 0) / 2) })} className="rounded-xl flex-1">Setengah Sisa</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl">Batal</Button>
            <Button data-testid="save-payment-btn" disabled={busy || exceeds || amountNum <= 0} onClick={savePay} className="rounded-xl bg-emerald-500 hover:bg-emerald-600">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fixing} onOpenChange={(v) => !v && setFixing(null)}>
        <DialogContent className="rounded-2xl" data-testid="fix-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Koreksi Pembayaran · {fixing?.student_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-rose-700 bg-rose-50 rounded-xl p-3">Tercatat {rupiah(fixing?.amount_paid)} dari tagihan {rupiah(fixing?.amount_due)}. Masukkan total pembayaran yang benar (maksimal {rupiah(fixing?.amount_due)}).</p>
            <div><Label>Total Dibayar (benar)</Label><Input type="number" data-testid="fix-amount-input" value={fixAmount} onChange={(e) => setFixAmount(e.target.value)} className="mt-1 rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixing(null)} className="rounded-xl">Batal</Button>
            <Button data-testid="save-fix-btn" disabled={busy || Number(fixAmount) > (fixing?.amount_due || 0)} onClick={saveFix} className="rounded-xl bg-rose-500 hover:bg-rose-600">Simpan Koreksi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
